import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { getBklsReadiness, runBklsSandboxPurchase } from "@/lib/bkls-readiness.functions";
import type { SandboxRehearsalResult } from "@/lib/bkls-sandbox-rehearsal.types";


export const Route = createFileRoute("/_authenticated/admin/bkls/readiness")({
  head: () => ({
    meta: [
      { title: "BKLS Launch Readiness · Buddha KA" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BklsReadiness,
});

const card = "rounded-2xl border hairline bg-charcoal p-6";

const TONE: Record<string, string> = {
  ready: "border-emerald/40 text-emerald",
  attention: "border-gold/50 text-gold",
  blocked: "border-destructive/50 text-destructive",
  pass: "border-emerald/40 text-emerald",
  fail: "border-destructive/50 text-destructive",
};

function Pill({ status }: { status: string }) {
  return (
    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${TONE[status] ?? "hairline text-muted-foreground"}`}>
      {status}
    </span>
  );
}

function BklsReadiness() {
  const load = useServerFn(getBklsReadiness);
  const report = useQuery({
    queryKey: ["bkls", "readiness"],
    queryFn: () => load(),
  });

  const data = report.data;

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Licensing</p>
          <h1 className="mt-2 font-serif text-3xl">Launch readiness.</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Live configuration state plus a real sandbox transaction rehearsal. No live charge is made and no
            public content is created — the rehearsal runs against an in-memory stand-in.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/bkls" className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold">
            ← Overview
          </Link>
          <button
            type="button"
            onClick={() => void report.refetch()}
            className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold"
            disabled={report.isFetching}
          >
            {report.isFetching ? "Running…" : "Re-run checks"}
          </button>
        </div>
      </header>

      {report.isLoading ? (
        <div className={card}>
          <p className="text-sm text-muted-foreground">Running readiness checks…</p>
        </div>
      ) : report.isError ? (
        <div className={`${card} border-destructive/50`}>
          <p className="text-sm text-destructive">
            Could not load the readiness report. {(report.error as Error)?.message}
          </p>
        </div>
      ) : !data ? (
        <div className={card}>
          <p className="text-sm text-muted-foreground">No report available yet.</p>
        </div>
      ) : (
        <>
          <section className={`${card} ${data.readyToLaunch ? "border-emerald/40" : "border-destructive/40"}`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Verdict</p>
                <p className="mt-1 font-serif text-2xl">
                  {data.readyToLaunch ? "Cleared for launch." : "Not ready for launch."}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {data.readyToLaunch
                    ? "Every blocking check passed. Review the attention items before flipping public visibility."
                    : `Blocking: ${data.blockers.join(", ")}`}
                </p>
              </div>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
                {Object.entries(data.counts).map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</dt>
                    <dd className="text-ivory">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Generated {new Date(data.generatedAt).toLocaleString()}
            </p>
          </section>

          <section className={card}>
            <h2 className="font-serif text-xl">Readiness checks</h2>
            <ul className="mt-4 space-y-3">
              {data.checks.map((check) => (
                <li key={check.key} className="rounded-xl border hairline bg-obsidian p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-ivory">{check.label}</p>
                    <Pill status={check.status} />
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{check.detail}</p>
                  {check.action ? (
                    <p className="mt-2 text-xs text-gold">Next step: {check.action}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>

          <section className={card}>
            <h2 className="font-serif text-xl">Transaction rehearsal</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Ran {new Date(data.rehearsal.ranAt).toLocaleString()} — issuance, duplicate-webhook safety, agreement
              immutability, owner-scoped library, signed downloads, refund revocation, retry safety and notification
              gating.
            </p>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {data.rehearsal.steps.map((step) => (
                <li key={step.key} className="rounded-xl border hairline bg-obsidian p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-ivory">{step.label}</p>
                    <Pill status={step.status} />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">{step.detail}</p>
                </li>
              ))}
            </ul>
          </section>

          <SandboxPurchasePanel />
        </>
      )}
    </div>
  );
}

function SandboxPurchasePanel() {
  const run = useServerFn(runBklsSandboxPurchase);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SandboxRehearsalResult | null>(null);

  return (
    <section className={card}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-xl">Stripe sandbox purchase &amp; email rehearsal</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Runs a real Stripe <strong>test mode</strong> purchase of “Think — Basic” through the production commerce
            path: checkout session, payment, signed webhook, licence issuance, agreement, entitlement and the purchase
            email. Public checkout stays disabled, no live charge is possible, and the resulting order is marked
            sandbox so it never counts as revenue.
          </p>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              setResult((await run({ data: { origin: window.location.origin } })) as SandboxRehearsalResult);
            } catch (e) {
              setError(e instanceof Error ? e.message : "Rehearsal failed to start.");
            } finally {
              setBusy(false);
            }
          }}
          className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian disabled:opacity-60"
        >
          {busy ? "Running rehearsal…" : "Run sandbox purchase"}
        </button>
      </div>

      {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}

      {result ? (
        <div className="mt-6 space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <Pill status={result.passed ? "pass" : "fail"} />
            <p className="text-sm text-muted-foreground">
              {new Date(result.ranAt).toLocaleString()} · {result.beatSlug} / {result.tierKey} ·{" "}
              {(result.amountCents / 100).toFixed(2)} {result.currency.toUpperCase()}
            </p>
          </div>

          <ul className="grid gap-3 sm:grid-cols-2">
            {result.steps.map((step) => (
              <li key={step.key} className="rounded-xl border hairline bg-obsidian p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-ivory">{step.label}</p>
                  <Pill status={step.status} />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{step.detail}</p>
              </li>
            ))}
          </ul>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border hairline bg-obsidian p-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Evidence</p>
              <dl className="mt-2 space-y-1 text-xs text-muted-foreground">
                {Object.entries(result.evidence).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <dt>{k.replace(/_/g, " ")}</dt>
                    <dd className="text-ivory">{v ?? "—"}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="rounded-xl border hairline bg-obsidian p-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Email &amp; cleanup</p>
              <p className="mt-2 text-xs text-muted-foreground">{result.emailResult}</p>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {result.cleanup.map((c) => (
                  <li key={c}>· {c}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

